import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useConvertToContract } from '../../hooks/useProjects';
import { apiErrorMessage } from '../../lib/api/apiErrorMessage';
import { useTenantId } from '../../pages/Delivery/_shared/hooks/useTenantId';
import { businessUnitHooks } from '../../pages/Delivery/Org/hooks';
const CONTRACT_TYPES = [
    {
        value: 'FFP',
        label: 'Firm Fixed Price (FFP)'
    },
    {
        value: 'T&M',
        label: 'Time & Materials (T&M)'
    },
    {
        value: 'CPFF',
        label: 'Cost Plus Fixed Fee (CPFF)'
    },
    {
        value: 'CPIF',
        label: 'Cost Plus Incentive Fee (CPIF)'
    },
    {
        value: 'CPAF',
        label: 'Cost Plus Award Fee (CPAF)'
    },
    {
        value: 'IDIQ',
        label: 'Indefinite Delivery/Indefinite Quantity (IDIQ)'
    },
    {
        value: 'BPA',
        label: 'Blanket Purchase Agreement (BPA)'
    },
    {
        value: 'HYBRID',
        label: 'Hybrid'
    },
    {
        value: 'OTHER',
        label: 'Other'
    }
];
export function ConvertToContractDialog({ open, onOpenChange, project }) {
    const navigate = useNavigate();
    const convertMutation = useConvertToContract();
    const tenantId = useTenantId();
    const businessUnits = businessUnitHooks.useList({
        companyId: tenantId
    });
    const [name, setName] = useState(`[Contract] ${project.name}`);
    const [contractNumber, setContractNumber] = useState('');
    const [contractType, setContractType] = useState('');
    const [archiveSource, setArchiveSource] = useState(false);
    const [businessUnitId, setBusinessUnitId] = useState('');
    const [touched, setTouched] = useState(false);
    const trimmedName = name.trim();
    const isNameEmpty = trimmedName.length === 0;
    const showNameError = touched && isNameEmpty;
    const isValid = !isNameEmpty;
    useEffect(()=>{
        if (open) {
            setName(`[Contract] ${project.name}`);
            setContractNumber('');
            setContractType('');
            setArchiveSource(false);
            setBusinessUnitId('');
            setTouched(false);
        }
    }, [
        open,
        project
    ]);
    const handleSubmit = async (e)=>{
        e.preventDefault();
        setTouched(true);
        if (!isValid) return;
        try {
            const result = await convertMutation.mutateAsync({
                projectId: project.projectId,
                name: trimmedName,
                contractNumber: contractNumber.trim() || undefined,
                contractType: contractType || undefined,
                archiveSourceProposal: archiveSource,
                businessUnitId: businessUnitId || undefined
            });
            const summary = result.conversionSummary;
            toast.success(`Contract project created. Carried forward: ${summary.requirementsCopied} requirements, ${summary.clinsCopied} CLINs, ${summary.deliverablesCopied} deliverables.`);
            if (summary.warnings?.length > 0) {
                for (const warning of summary.warnings){
                    toast.warning(warning);
                }
            }
            onOpenChange(false);
            navigate(`/contract/${result.project.projectId}`);
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Failed to convert proposal to contract'));
        }
    };
    return /*#__PURE__*/ _jsx(Dialog, {
        open: open,
        onOpenChange: onOpenChange,
        children: /*#__PURE__*/ _jsxs(DialogContent, {
            className: "sm:max-w-[500px]",
            "data-testid": "convert-to-contract-dialog",
            children: [
                /*#__PURE__*/ _jsxs(DialogHeader, {
                    children: [
                        /*#__PURE__*/ _jsx(DialogTitle, {
                            children: "Convert to Contract"
                        }),
                        /*#__PURE__*/ _jsx(DialogDescription, {
                            children: "Create a new contract project from this proposal. Requirements, CLINs, deliverables, and source documents will be carried forward."
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("form", {
                    onSubmit: handleSubmit,
                    className: "space-y-4",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsxs("label", {
                                    htmlFor: "contract-name",
                                    className: "text-sm font-medium",
                                    children: [
                                        "Contract Project Name ",
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "text-red-500",
                                            children: "*"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("input", {
                                    id: "contract-name",
                                    type: "text",
                                    value: name,
                                    onChange: (e)=>setName(e.target.value),
                                    onBlur: ()=>setTouched(true),
                                    placeholder: "e.g., [Contract] NASA Cloud Services",
                                    required: true,
                                    maxLength: 200,
                                    className: `w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 ${showNameError ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-indigo-500'}`,
                                    disabled: convertMutation.isPending,
                                    "data-testid": "convert-contract-name-input"
                                }),
                                showNameError && /*#__PURE__*/ _jsx("p", {
                                    className: "text-xs text-red-500",
                                    role: "alert",
                                    children: "Contract name is required"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsxs("label", {
                                    htmlFor: "contract-number",
                                    className: "text-sm font-medium",
                                    children: [
                                        "Contract Number ",
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "text-gray-400",
                                            children: "(optional)"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("input", {
                                    id: "contract-number",
                                    type: "text",
                                    value: contractNumber,
                                    onChange: (e)=>setContractNumber(e.target.value),
                                    placeholder: "e.g., GS-35F-0001X",
                                    maxLength: 100,
                                    className: "w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500",
                                    disabled: convertMutation.isPending,
                                    "data-testid": "convert-contract-number-input"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsxs("label", {
                                    htmlFor: "contract-type",
                                    className: "text-sm font-medium",
                                    children: [
                                        "Contract Type ",
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "text-gray-400",
                                            children: "(optional)"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("select", {
                                    id: "contract-type",
                                    value: contractType,
                                    onChange: (e)=>setContractType(e.target.value),
                                    className: "w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500",
                                    disabled: convertMutation.isPending,
                                    "data-testid": "convert-contract-type-select",
                                    children: [
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "",
                                            children: "Select contract type..."
                                        }),
                                        CONTRACT_TYPES.map((ct)=>/*#__PURE__*/ _jsx("option", {
                                                value: ct.value,
                                                children: ct.label
                                            }, ct.value))
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsxs("label", {
                                    htmlFor: "contract-business-unit",
                                    className: "text-sm font-medium",
                                    children: [
                                        "Business Unit ",
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "text-gray-400",
                                            children: "(optional)"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("select", {
                                    id: "contract-business-unit",
                                    value: businessUnitId,
                                    onChange: (e)=>setBusinessUnitId(e.target.value),
                                    className: "w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500",
                                    disabled: convertMutation.isPending,
                                    "data-testid": Testids.deliveryPrograms.businessUnitSelect,
                                    children: [
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "",
                                            children: "Company-wide (no business unit)"
                                        }),
                                        (businessUnits.data?.items ?? []).map((bu)=>/*#__PURE__*/ _jsx("option", {
                                                value: bu.id,
                                                children: bu.name
                                            }, bu.id))
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-xs text-gray-500",
                                    children: "Scopes the new contract's delivery program to this business unit. Requires the delivery-VP bundle for that unit (or company-wide) to create it."
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-2",
                            children: [
                                /*#__PURE__*/ _jsx("input", {
                                    id: "archive-source",
                                    type: "checkbox",
                                    checked: archiveSource,
                                    onChange: (e)=>setArchiveSource(e.target.checked),
                                    className: "h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500",
                                    disabled: convertMutation.isPending,
                                    "data-testid": "convert-archive-checkbox"
                                }),
                                /*#__PURE__*/ _jsx("label", {
                                    htmlFor: "archive-source",
                                    className: "text-sm text-gray-700",
                                    children: "Archive source proposal after conversion"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(DialogFooter, {
                            className: "gap-2",
                            children: [
                                /*#__PURE__*/ _jsx(Button, {
                                    type: "button",
                                    variant: "outline",
                                    onClick: ()=>onOpenChange(false),
                                    disabled: convertMutation.isPending,
                                    "data-testid": "convert-cancel-button",
                                    children: "Cancel"
                                }),
                                /*#__PURE__*/ _jsx(Button, {
                                    type: "submit",
                                    isLoading: convertMutation.isPending,
                                    disabled: convertMutation.isPending || touched && !isValid,
                                    "data-testid": "convert-submit-button",
                                    children: "Convert to Contract"
                                })
                            ]
                        })
                    ]
                })
            ]
        })
    });
}
